import type { PartnerContract } from '@qlk/shared';
import { useState } from 'react';

import { Nut, O, homNayVn, ngayDay } from '../../../ui/index.js';
import { useSuaHopDong, useTaoPhienBanHopDong, useTinhHinhHopDong } from '../api.js';
import { HopThoaiXoaHopDong, LyDoKhongXoaDuoc } from './HopThoaiXoaHopDong.js';

/**
 * Nút Sửa / Phiên bản mới / Xoá (hoặc Kết thúc) của một dòng hợp đồng.
 *
 * Hợp đồng **đã dùng chia tiền** (kỳ đã chốt, sao kê đã xử lý) thì ô "Network
 * giữ" và "ngày hiệu lực" bị khoá — sửa đè hai trường đó làm số báo cáo kỳ cũ
 * đổi theo. Lúc đó dùng "Tạo phiên bản mới từ ngày…".
 *
 * Xoá theo ba mức (`mucDoXoa`): xoá ngay, xoá sau khi chọn chỗ đi cho từng kênh
 * đang gắn, hoặc không xoá được vì đã chia tiền — lúc đó mời "Kết thúc hợp
 * đồng" và nêu rõ kỳ / sao kê liên quan.
 */

const ngayCuaIso = (iso: string | null): string => (iso === null ? '' : iso.slice(0, 10));

export function HangHopDong({
  hopDong,
  adsense = false,
}: {
  hopDong: PartnerContract;
  /** Tài khoản AdSense: không có tỷ lệ chia, ô "Network giữ" ẩn hẳn. */
  adsense?: boolean;
}) {
  const [che, datChe] = useState<'dong' | 'sua' | 'phien-ban' | 'xoa' | 'vi-sao'>('dong');

  const tinhHinh = useTinhHinhHopDong(hopDong.id);
  const sua = useSuaHopDong(hopDong.id);
  const phienBan = useTaoPhienBanHopDong(hopDong.id);

  const khoaTyLe = tinhHinh.data?.daDungChiaTien === true;
  const mucDoXoa = tinhHinh.data?.mucDoXoa;
  const xoaDuoc = mucDoXoa === 'ngay' || mucDoXoa === 'can-chuyen-kenh';

  function ketThucHopDong(): void {
    sua.mutate(
      { endDate: `${homNayVn()}T00:00:00.000Z`, status: 'expired' },
      {
        onSuccess: () => {
          datChe('dong');
        },
      },
    );
  }

  if (che === 'sua') {
    return (
      <FormSuaHopDong
        hopDong={hopDong}
        adsense={adsense}
        khoaTyLe={khoaTyLe}
        dangChay={sua.isPending}
        loi={sua.error?.message ?? null}
        onDong={() => {
          datChe('dong');
          sua.reset();
        }}
        onLuu={(input) => {
          sua.mutate(input, {
            onSuccess: () => {
              datChe('dong');
            },
          });
        }}
      />
    );
  }

  if (che === 'phien-ban') {
    return (
      <FormPhienBan
        hopDong={hopDong}
        adsense={adsense}
        dangChay={phienBan.isPending}
        loi={phienBan.error?.message ?? null}
        onDong={() => {
          datChe('dong');
          phienBan.reset();
        }}
        onLuu={(tuNgay, tyLe, thueMy) => {
          phienBan.mutate(
            {
              tuNgay: `${tuNgay}T00:00:00.000Z`,
              ...(adsense ? {} : { defaultSharePercent: tyLe }),
              thueMyPct: thueMy,
            },
            {
              onSuccess: () => {
                datChe('dong');
              },
            },
          );
        }}
      />
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Nut
        kieu="phu"
        onClick={() => {
          datChe('sua');
        }}
      >
        Sửa
      </Nut>

      {khoaTyLe && (
        <Nut
          kieu="phu"
          onClick={() => {
            datChe('phien-ban');
          }}
        >
          Tạo phiên bản mới
        </Nut>
      )}

      {xoaDuoc && (
        <Nut
          kieu="phu"
          onClick={() => {
            datChe('xoa');
          }}
        >
          Xoá
        </Nut>
      )}

      {mucDoXoa === 'chan' && (
        <>
          <Nut kieu="phu" dangChay={sua.isPending} onClick={ketThucHopDong}>
            Kết thúc hợp đồng
          </Nut>
          <Nut
            kieu="phu"
            onClick={() => {
              datChe('vi-sao');
            }}
          >
            Vì sao không xoá được?
          </Nut>
        </>
      )}

      {mucDoXoa === 'chan' && (tinhHinh.data?.lyDoKhongXoaDuoc.length ?? 0) > 0 && (
        <p className="w-full text-right text-phu text-chu-mo">
          Không xoá được: {tinhHinh.data?.lyDoKhongXoaDuoc.join(', ')}. Kết thúc hợp đồng thì lịch
          sử chia tiền vẫn giữ.
        </p>
      )}

      {mucDoXoa === 'can-chuyen-kenh' && (tinhHinh.data?.soKenhDangGan ?? 0) > 0 && (
        <p className="w-full text-right text-phu text-chu-mo">
          Hợp đồng còn {tinhHinh.data?.soKenhDangGan} kênh gắn — xoá được, nhưng phải chọn chỗ đi
          cho từng kênh.
        </p>
      )}

      {che === 'xoa' && tinhHinh.data && (
        <HopThoaiXoaHopDong
          hopDong={hopDong}
          tinhHinh={tinhHinh.data}
          onDong={() => {
            datChe('dong');
          }}
        />
      )}

      {che === 'vi-sao' && tinhHinh.data && (
        <LyDoKhongXoaDuoc
          tinhHinh={tinhHinh.data}
          dangKetThuc={sua.isPending}
          onKetThuc={ketThucHopDong}
          onDong={() => {
            datChe('dong');
          }}
        />
      )}

      {sua.error && che === 'dong' && (
        <p role="alert" className="w-full text-right text-phu text-bad-chu">
          {sua.error.message}
        </p>
      )}
    </div>
  );
}

interface GiaTriSua {
  contractNo?: string;
  startDate?: string;
  endDate?: string | null;
  defaultSharePercent?: number;
  thueMyPct?: number;
  paymentTerms?: string | null;
  paymentThreshold?: number | null;
}

function FormSuaHopDong({
  hopDong,
  adsense,
  khoaTyLe,
  dangChay,
  loi,
  onDong,
  onLuu,
}: {
  hopDong: PartnerContract;
  adsense: boolean;
  khoaTyLe: boolean;
  dangChay: boolean;
  loi: string | null;
  onDong: () => void;
  onLuu: (input: GiaTriSua) => void;
}) {
  const [so, datSo] = useState(hopDong.contractNo);
  const [tuNgay, datTuNgay] = useState(ngayCuaIso(hopDong.startDate));
  const [denNgay, datDenNgay] = useState(ngayCuaIso(hopDong.endDate));
  const [tyLe, datTyLe] = useState(hopDong.defaultSharePercent);
  const [thueMy, datThueMy] = useState(hopDong.thueMyPct);
  const [ky, datKy] = useState(hopDong.paymentTerms ?? '');
  const [nguong, datNguong] = useState(hopDong.paymentThreshold ?? '');

  return (
    <form
      className="flex flex-wrap items-end justify-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onLuu({
          contractNo: so.trim(),
          ...(khoaTyLe
            ? {}
            : {
                startDate: `${tuNgay}T00:00:00.000Z`,
                thueMyPct: Number(thueMy),
                ...(adsense ? {} : { defaultSharePercent: Number(tyLe) }),
              }),
          endDate: denNgay === '' ? null : `${denNgay}T00:00:00.000Z`,
          paymentTerms: ky.trim() === '' ? null : ky.trim(),
          paymentThreshold: nguong === '' ? null : Number(nguong),
        });
      }}
    >
      <O label="Số hợp đồng" value={so} onChange={datSo} required className="w-[160px]" />
      {/*
       * Ô bị khoá phải **tắt hẳn**, không chỉ ghi chú: trước đây ô vẫn gõ được
       * mà submit lại bỏ qua giá trị, nên người dùng đổi ngày rồi bấm Lưu và
       * không thấy gì đổi (cùng họ lỗi với ô ngày "nhập dở" ở `docGiaTriO`).
       */}
      <O
        label="Ngày hiệu lực"
        type="date"
        value={tuNgay}
        max={homNayVn()}
        onChange={datTuNgay}
        disabled={khoaTyLe}
        {...(khoaTyLe ? { moTa: 'Đã dùng chia tiền — dùng "Tạo phiên bản mới".' } : {})}
        className="w-[150px]"
      />
      <O
        label="Ngày kết thúc"
        type="date"
        value={denNgay}
        onChange={datDenNgay}
        moTa="Bỏ trống = không thời hạn."
        className="w-[150px]"
      />
      {!adsense && (
        <O
          label="Network giữ (%)"
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={tyLe}
          onChange={datTyLe}
          disabled={khoaTyLe}
          className="w-[140px]"
        />
      )}
      <O
        label="Thuế Mỹ (%)"
        type="number"
        min={0}
        max={100}
        step={0.5}
        value={thueMy}
        onChange={datThueMy}
        disabled={khoaTyLe}
        className="w-[130px]"
      />
      {!adsense && <O label="Kỳ thanh toán" value={ky} onChange={datKy} className="w-[150px]" />}
      <O
        label="Ngưỡng chi trả"
        type="number"
        min={0}
        value={nguong}
        onChange={datNguong}
        className="w-[140px]"
      />

      <Nut kieu="chinh" type="submit" dangChay={dangChay}>
        Lưu
      </Nut>
      <Nut kieu="phu" onClick={onDong}>
        Huỷ
      </Nut>

      {khoaTyLe && (
        <p className="w-full text-right text-phu text-chu-mo">
          Hợp đồng đã dùng chia tiền: ô {adsense ? '"Thuế Mỹ"' : '"Network giữ", "Thuế Mỹ"'} và
          "Ngày hiệu lực" không lưu, hãy tạo phiên bản mới.
        </p>
      )}
      {loi !== null && (
        <p role="alert" className="w-full text-right text-phu text-bad-chu">
          {loi}
        </p>
      )}
    </form>
  );
}

function FormPhienBan({
  hopDong,
  adsense,
  dangChay,
  loi,
  onDong,
  onLuu,
}: {
  hopDong: PartnerContract;
  adsense: boolean;
  dangChay: boolean;
  loi: string | null;
  onDong: () => void;
  onLuu: (tuNgay: string, tyLe: number, thueMy: number) => void;
}) {
  const [tuNgay, datTuNgay] = useState(homNayVn());
  const [tyLe, datTyLe] = useState(hopDong.defaultSharePercent);
  const [thueMy, datThueMy] = useState(hopDong.thueMyPct);

  return (
    <form
      className="flex flex-wrap items-end justify-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onLuu(tuNgay, Number(tyLe), Number(thueMy));
      }}
    >
      <O
        label="Phiên bản mới từ ngày"
        type="date"
        value={tuNgay}
        onChange={datTuNgay}
        moTa={`Bản ${hopDong.contractNo} sẽ kết thúc ngày hôm trước.`}
        className="w-[190px]"
      />
      {!adsense && (
        <O
          label="Network giữ (%)"
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={tyLe}
          onChange={datTyLe}
          className="w-[140px]"
        />
      )}
      <O
        label="Thuế Mỹ (%)"
        type="number"
        min={0}
        max={100}
        step={0.5}
        value={thueMy}
        onChange={datThueMy}
        className="w-[130px]"
      />

      <Nut kieu="chinh" type="submit" dangChay={dangChay}>
        Tạo phiên bản
      </Nut>
      <Nut kieu="phu" onClick={onDong}>
        Huỷ
      </Nut>

      <p className="w-full text-right text-phu text-chu-mo">
        Kỳ cũ giữ nguyên tỷ lệ và thuế Mỹ cũ: báo cáo chia theo ngày của từng hợp đồng. Hợp đồng
        hiện tại hiệu lực từ {ngayDay(hopDong.startDate)}.
      </p>
      {loi !== null && (
        <p role="alert" className="w-full text-right text-phu text-bad-chu">
          {loi}
        </p>
      )}
    </form>
  );
}
